import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { Button } from "antd";

import {
  adminDashboardUrl,
  tomAppUrl,
  jeAppUrl,
  ciAppUrl,
  rbAppUrl,
  listTom,
  listLivePayBenchmarking,
} from "@/constants";
import { paths } from "@/router";
import { useVerifyMutation } from "@/services";
import { useTypedSelector } from "@/hooks";
import { setSelectedAppInRedux } from "@/store/selected_app";
import { IAppType } from "@/types";
import "./apps.less";
import AppRow from "./appRow";
import { setSelectedApplicationInCookies } from "@/services/global-storage";
import Logo from "@assets/images/logoDDark.png";

import DashboardIcon from "@/assets/images/dashboard.png";
import TomIcon from "@/assets/images/tom.png";
import TarefIcon from "@/assets/images/taref.png";
import BenchmarkIcon from "@/assets/images/benchmark.png";

import homeArt from "@/assets/images/homeArt.png";

interface IApps {}

const Apps = (props: IApps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [selectedApp, setSelectedApp] = useState<IAppType>();
  const [verifyUser] = useVerifyMutation();

  const userApplications = useTypedSelector(state => state.auth.applications);
  const companyId =
    useTypedSelector(state => state.auth.user?.company?.id) || 0;

  const isCompanyUser = useTypedSelector(
    state => state.auth.user?.is_company_user
  );

  useEffect(() => {
    verifyUser({ companyId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasAppPermission = (appId: number): boolean => {
    return isCompanyUser
      ? userApplications.some(app => app.id === appId)
      : true;
  };
  const appsList: IAppType[] = [
    ...(isCompanyUser
      ? []
      : [
          {
            id: 0,
            name: "Dashboard",
            desc: "Admin Dashboard",
            initials: "D",
            disabled: false,
            applicationLink: ciAppUrl,
            key: "ADMIN_PANEL",
          },
        ]),
    ...(listTom
      ? [
          {
            id: 1,
            name: "TOM",
            desc: "TOM Application",
            initials: "T",
            disabled: !hasAppPermission(1),
            applicationLink: tomAppUrl,
            key: "TOM",
          },
        ]
      : []),
    {
      id: 2,
      name: "TAREF",
      desc: "Job Evaluation Application",
      initials: "JE",
      disabled: !hasAppPermission(2),
      applicationLink: jeAppUrl,
      key: "JE",
    },
    ...(listLivePayBenchmarking
      ? [
          {
            id: 3,
            name: "Live Pay Benchmarking",
            desc: "Reporting & Benchmark Application",
            initials: "RB",
            disabled: !hasAppPermission(3),
            applicationLink: rbAppUrl,
            key: "LIVE_PAY_BENCHMARKING_TOOL",
          },
        ]
      : []),
  ];

  const onClickNext = () => {
    if (isCompanyUser || selectedApp?.id === 0)
      window.open(selectedApp?.applicationLink!, "_self");
    else {
      history.push(paths.ssoRoute.company.select_company);
    }
  };
  const onAppSelect = (app: IAppType) => {
    if (!app.disabled) {
      setSelectedApp(app);
      setSelectedApplicationInCookies(app.key);
      dispatch(setSelectedAppInRedux(app));
      onClickNext();
    }
  };

  // return (
  //   <div className="ApplicationSelector flex flex-col items-center">
  //     <h2 className="font-bold text-6xl text-themeBlue mt-6 ">
  //       Select the Application
  //     </h2>
  //     <div
  //       style={{
  //         width: "50rem",
  //       }}
  //       className="selector bg-white rounded-md border-4 border-themeBlue py-20 px-12 mt-16  flex flex-wrap justify-stretch gap-6 gap-y-10"
  //     >
  //       {appsList?.map((app, index) => (
  //         <AppRow
  //           app={app}
  //           isSelected={app.name === selectedApp?.name}
  //           onAppSelect={onAppSelect}
  //           key={app.name}
  //         />
  //       ))}
  //     </div>
  //   </div>
  // );

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="relative z-10 max-w-6xl mx-auto px-8 py-12">
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <img
            src={Logo}
            alt="Compensation Core"
            className="h-12 object-contain"
          />
        </div>

        {/* Heading */}
        <div className="text-center mb-14">
          <h1 className="text-4xl font-semibold text-slate-800">
            Select the Tool
          </h1>
        </div>

        {/* Apps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 z-50">
          {appsList?.map(app => (
            <div
              style={{
                background: "#F9F9F9",
              }}
              key={app.name}
              onClick={() => onAppSelect(app)}
              className={`
                 group
              relative
              rounded-3xl
              border-slate-200
              cursor-pointer
              transition-all
              duration-300
              min-h-[180px]
              shadow-sm
              flex 
              items-center
              gap-5
              ${
                app.disabled
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:-translate-y-1 hover:shadow-xl hover: border-blue-300"
              }
            `}
            >
              {/* Icon */}
              <div
                className={`
    h-32
    w-36
    rounded-2xl
    flex
    items-center
    justify-center
    transition-colors
    duration-300
    bg-themeCore
    ${!app.disabled ? "group-hover:bg-themeOrange" : ""}
  `}
              >
                <img
                  src={
                    app.id === 0
                      ? DashboardIcon
                      : app.id === 1
                      ? TomIcon
                      : app.id === 2
                      ? TarefIcon
                      : BenchmarkIcon
                  }
                  alt={app.name}
                  className="h-16 w-20 object-contain"
                />
              </div>

              {/* Content */}
              <div className="">
                <h3 className="text-xl font-bold" style={{ color: "#04048C" }}>
                  {app.name}
                </h3>

                <p className="mt-2" style={{ color: "#9E9E9E" }}>
                  {app.desc}
                </p>
              </div>

              {app.disabled && (
                <div className="absolute top-4 right-4">
                  <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-500">
                    No Access
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-0 right-0 max-w-lg z-0 pointer-events-none">
        <img src={homeArt} className="w-full h-full object-contain" alt="art" />
      </div>
    </div>
  );
};

export default Apps;
